import React from "react";
import TopBanner from "../components/TopBanner";
import { CTAButton, CTAButtonWrapper } from "../components/HeroSection";
import { ContentWrapper } from "./InsuranceGuidance";
import { Card, Cards, Icon } from "../components/WhoWeAre";
import { CardText, CardTitle } from "../components/WhyChooseUS";
import styled from "styled-components";
import FAQ from "../components/FAQ";

const faqData = [
  {
    question: "What is the ideal age to buy term insurance?",
    answer:
      "Many individuals consider purchasing term insurance earlier in their working life because premiums are generally lower at younger ages.",
  },
  {
    question: "Can non-working individuals buy term insurance?",
    answer:
      "Some insurers may allow non-working individuals to purchase policies, but eligibility and coverage limits may vary.",
  },
  {
    question: "Is medical examination mandatory?",
    answer:
      "Medical tests may be required depending on factors such as age, coverage amount, and insurer policies.",
  },
  {
    question: "Can term insurance be cancelled?",
    answer:
      "Most insurers allow policy cancellation within a specified free-look period. Terms may vary between insurers.",
  },
];

const TermInsurance = ({ openModal }) => {
  React.useEffect(() => {
    document.title =
      "Term Insurance Guide – Meaning, Benefits, Eligibility & Tax Benefits in India | Wealthies";
  }, []);

  return (
    <>
      <TopBanner content={"Term Insurance"} />
      <ContentWrapper>
        <div className="text-content">
          <h2>Introduction</h2>
          <p>
            Term insurance is one of the simplest and most widely discussed
            forms of life insurance. It is designed to provide financial
            protection to your family in case of an unexpected event during the
            policy term.
          </p>
          <p>
            A term insurance policy provides coverage for a fixed period. If the
            insured person passes away during the policy term, the nominee may
            receive the sum assured from the insurance company, subject to
            policy terms and conditions.
          </p>
          <p>
            Understanding how term insurance works can help individuals evaluate
            whether such coverage may be suitable for their financial protection
            planning.
          </p>
          <p>
            This guide explains the basics of term insurance, including
            eligibility, benefits, factors affecting premiums, and possible tax
            benefits under Indian tax laws.
          </p>

          <h3>
            What is <span>Term Insurance</span>
          </h3>
          <p>
            Term insurance is a type of life insurance policy that provides
            financial protection for a specific period, known as the policy
            term.
          </p>
          <p>
            The policyholder pays a premium to the insurance company for the
            coverage. If the insured person passes away during the policy term,
            the nominee may receive the insured amount, subject to the terms and
            conditions of the policy.
          </p>
          <p>
            Unlike some other life insurance policies, term insurance is
            generally focused on providing financial protection rather than
            investment returns.
          </p>

          <h3>
            Why Many People Consider <span>Term Insurance</span>
          </h3>
          <p>
            Many individuals consider term insurance as part of financial
            protection planning because it can help provide financial support to
            dependents in case of unforeseen events.
          </p>
          <Cards>
            <Card>
              <Icon className="fas fa-user-shield"></Icon>
              <CardText>
                Helping provide financial protection for family members
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-users"></Icon>
              <CardText>
                Supporting dependents in managing daily expenses
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-file-invoice-dollar"></Icon>
              <CardText>
                Assisting in managing financial obligations such as loans
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-briefcase"></Icon>
              <CardText>
                Providing financial security during working years
              </CardText>
            </Card>
          </Cards>
          <p>
            The suitability of any insurance policy depends on individual needs
            and financial circumstances.
          </p>

          <h3>
            Eligibility <span>for Term Insurance</span> in India
          </h3>
          <p>
            Eligibility criteria for term insurance may vary between insurance
            companies, but most insurers follow general guidelines.
          </p>

          <h5>Age Eligibility</h5>
          <p>
            Most insurance companies allow individuals to purchase term
            insurance within a certain age range.
          </p>
          <ul>
            <li>Minimum entry age: around 18 years</li>
            <li>
              Maximum entry age: usually between 55 to 65 years, depending on
              the insurer and policy.
            </li>
          </ul>

          <h5>Income Requirement</h5>
          <p>
            Some insurers require applicants to demonstrate a stable source of
            income. This helps insurers assess the appropriate coverage amount.
          </p>
          <ul>
            <li>Salary slips</li>
            <li>Income tax returns</li>
            <li>Bank statements</li>
          </ul>

          <h5>Medical Assessment</h5>
          <p>
            Depending on the age, coverage amount, and insurer policies, medical
            tests may be required before issuing the policy.
          </p>
          <p>
            Medical assessments help insurers evaluate health conditions and
            risk factors.
          </p>

          <h5>Documentation Requirements</h5>
          <p>
            Applicants usually need to submit identity and financial documents
            such as:
          </p>
          <ul>
            <li>PAN card</li>
            <li>Aadhaar card</li>
            <li>Address proof</li>
            <li>Income proof</li>
          </ul>
          <p>Documentation requirements may vary between insurers.</p>

          <h3>
            Key Benefits of <span>Term Insurance</span>
          </h3>
          <Cards>
            <Card>
              <Icon className="fas fa-user-shield"></Icon>
              <CardTitle>Financial Protection for Dependents</CardTitle>
              <CardText>
                Term insurance may provide financial support to family members
                if the insured person passes away during the policy term.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-rupee-sign"></Icon>
              <CardTitle>Affordable Premium Structure</CardTitle>
              <CardText>
                Compared to some other life insurance products, term insurance
                policies often offer higher coverage amounts for relatively
                lower premiums.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-sliders-h"></Icon>
              <CardTitle>Flexible Policy Terms</CardTitle>
              <CardText>
                Many insurers offer flexible policy terms that allow individuals
                to select coverage periods based on their financial planning
                needs.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-plus-circle"></Icon>
              <CardTitle>Optional Riders</CardTitle>
              <CardText>
                Some insurers offer additional coverage options called riders:
              </CardText>

              <SubList>
                <li>
                  <i className="fas fa-car-crash"></i> Accidental death benefit
                  rider
                </li>
                <li>
                  <i className="fas fa-heartbeat"></i> Critical illness rider
                </li>
                <li>
                  <i className="fas fa-wheelchair"></i> Disability rider
                </li>
              </SubList>
            </Card>
          </Cards>
          <p>
            These riders may provide additional coverage, subject to policy
            terms.
          </p>

          <h3>
            Factors That Affect <span>Term Insurance Premiums</span>
          </h3>
          <Cards>
            <Card>
              <Icon className="fas fa-user-clock"></Icon>
              <CardTitle>Age</CardTitle>
              <CardText>
                Younger individuals often have lower premiums compared to older
                applicants because the risk profile is generally lower.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-heartbeat"></Icon>
              <CardTitle>Health Condition</CardTitle>
              <CardText>
                Medical history, lifestyle habits, and current health conditions
                may influence premium rates.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-layer-group"></Icon>
              <CardTitle>Coverage Amount</CardTitle>
              <CardText>
                Higher coverage amounts usually result in higher premiums.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-calendar-alt"></Icon>
              <CardTitle>Policy Term</CardTitle>
              <CardText>
                The duration of coverage selected may also affect premium
                calculations.
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-smoking"></Icon>
              <CardTitle>Lifestyle Factors</CardTitle>
              <CardText>
                Certain lifestyle factors such as smoking habits may impact
                premium rates.
              </CardText>
            </Card>
          </Cards>

          <h3>
            Tax Benefits of <span>Term Insurance</span> in India
          </h3>
          <h5>Section 80C – Premium Deduction</h5>
          <p>
            Premiums paid for eligible life insurance policies may qualify for
            tax deductions under Section 80C, up to the overall limit allowed
            under the section.
          </p>
          <p>
            This deduction may apply when premiums are paid for: self, spouse,
            or children.
          </p>

          <h5>Section 10(10D) – Tax Exemption on Claim Amount</h5>
          <p>
            Under certain conditions, the death benefit received by the nominee
            may be tax-exempt under Section 10(10D) of the Income Tax Act.
          </p>
          <p>
            Tax exemptions depend on policy conditions and prevailing tax
            regulations.
          </p>

          <h3>
            Common Mistakes People Make While Choosing{" "}
            <span>Term Insurance</span>
          </h3>
          <Cards>
            <Card>
              <Icon className="fas fa-shield-alt"></Icon>
              <CardText>Choosing insufficient coverage</CardText>
            </Card>

            <Card>
              <Icon className="fas fa-clock"></Icon>
              <CardText>Delaying insurance purchase</CardText>
            </Card>

            <Card>
              <Icon className="fas fa-file-alt"></Icon>
              <CardText>Not reviewing policy terms carefully</CardText>
            </Card>
          </Cards>
          <p>
            Understanding common mistakes can help individuals make
            better-informed decisions.
          </p>

          <h3>
            Frequently <span>Asked</span> Questions (FAQ)
          </h3>
          <FAQ faqData={faqData} />

          <h3>
            How <span>Wealthies</span> Helps
          </h3>
          <p>
            Wealthies aims to simplify insurance awareness by helping
            individuals understand insurance concepts in simple language.
          </p>
          <Cards>
            <Card>
              <Icon className="fas fa-book-open"></Icon>
              <CardText>Learn about insurance policies</CardText>
            </Card>

            <Card>
              <Icon className="fas fa-search"></Icon>
              <CardText>
                Understand factors to consider before purchasing coverage
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-user-tie"></Icon>
              <CardText>
                Connect with licensed insurance professionals for further
                guidance
              </CardText>
            </Card>

            <Card>
              <Icon className="fas fa-headset"></Icon>
              <CardText>
                Receive assistance through Wealthies Shield support
              </CardText>
            </Card>
          </Cards>

          <p>Still have questions about term insurance?</p>
          <p>
            Our team can help you understand the basics and connect you with
            licensed insurance professionals for further assistance.
          </p>

          <CTAButtonWrapper>
            <CTAButton onClick={openModal}>
              Get Free Insurance Guidance
            </CTAButton>
          </CTAButtonWrapper>
        </div>
      </ContentWrapper>
    </>
  );
};

export default TermInsurance;

export const SubList = styled.ul`
  margin-top: 10px;
  padding-left: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    margin-bottom: 6px;
  }

  i {
    color: #d4af37;
  }
`;
